/**
 * Telemetria desenhada para calibrar DEPOIS.
 *
 * Tres escolhas deliberadas, e as tres sao IMPOSSIVEIS DE RECONSTRUIR
 * RETROATIVAMENTE -- dados que nao foram registrados nao podem ser inferidos:
 *
 * 1. `templateId` E `seed` juntos. Permite calibrar em qualquer granularidade e,
 *    principalmente, TESTAR a hipotese de que as variantes sao equivalentes, em vez
 *    de assumi-la. Variantes geradas automaticamente nao sao psicometricamente
 *    equivalentes, e sem a seed a variante que o aluno viu e irrecuperavel.
 *
 * 2. `variantFeatures`. Vetor 0/1 sobre as fontes de dificuldade. Habilita um modelo
 *    TRI explicativo (LLTM): b_variante = b_template + soma(beta_k * feature_k). Voce
 *    aprende o custo empirico em logits de "mais uma etapa" no banco inteiro, e esses
 *    betas dao prior calibrado para templates novos ja no primeiro dia.
 *
 * 3. `latencyMs` + `evidence.matchedError` no MESMO registro. Habilita modelos de
 *    tempo de resposta e modelos de resposta nominal, em que cada distrator ganha sua
 *    propria curva. E aqui que a ancoragem em erros nomeados vira valor psicometrico:
 *    da para mostrar que um erro e mais escolhido por alunos de proficiencia MEDIA do
 *    que por alunos de proficiencia baixa -- diagnostico muito mais rico que "errou".
 *
 * Custa ~40 linhas de tipo agora. Fazer errado custa o primeiro ano de dados-piloto.
 */

import type { Seed } from '../core/rng.ts';
import type { ToolAction, ToolId } from '../geom/tools.ts';
import type { DifficultySourceCode } from './difficulty.ts';
import type { Evidence, Response } from './response.ts';
import type { InteractivityLevel, TemplateId } from './item.ts';
import type { ChoiceId, SkillCode } from './taxonomy.ts';

export type PlayerMode = 'practice' | 'exam' | 'review';

export interface EventEnvelope {
  /** Versao do esquema. Nunca omitir: e o que permite ao servidor futuro aceitar os dados de hoje. */
  readonly v: 1;
  readonly eventId: string;
  /** Monotonico dentro da tentativa. */
  readonly seq: number;
  /** Milissegundos desde o inicio da tentativa, de `performance.now()` -- NAO relogio de parede. */
  readonly tMs: number;
  /** Relogio de parede ISO, para ordenar entre dispositivos. */
  readonly emittedAt: string;
  readonly sessionId: string;
  readonly attemptId: string;
  readonly personId: string;
  readonly templateId: TemplateId;
  readonly templateVersion: number;
  readonly seed: Seed;
  readonly instanceHash: string;
}

export type TelemetryBody =
  | {
      readonly type: 'item.presented';
      readonly level: InteractivityLevel;
      readonly skills: readonly SkillCode[];
      readonly provisionalB: number;
      readonly variantFeatures: Readonly<Partial<Record<DifficultySourceCode, 1>>>;
      readonly mode: PlayerMode;
      readonly viewport: { readonly w: number; readonly h: number };
      readonly reducedMotion: boolean;
    }
  | { readonly type: 'item.focus'; readonly focused: boolean }
  | {
      readonly type: 'stimulus.interacted';
      readonly stimulusId: string;
      readonly control: string;
      readonly value: number;
    }
  | {
      readonly type: 'stimulus.playback';
      readonly stimulusId: string;
      readonly action: 'play' | 'pause' | 'seek' | 'replay';
      readonly atMs: number;
    }
  /** A troca de alternativa e sinal forte de habilidade; por isso guardamos a anterior. */
  | {
      readonly type: 'choice.selected';
      readonly choiceId: ChoiceId;
      readonly previous: ChoiceId | null;
    }
  | { readonly type: 'numeric.changed'; readonly raw: string }
  /** O aluno pediu a dica de número `index` (a partir de 0). */
  | { readonly type: 'hint.revealed'; readonly index: number }
  | { readonly type: 'tool.selected'; readonly tool: ToolId }
  | { readonly type: 'tool.applied'; readonly action: ToolAction }
  | { readonly type: 'construction.undo'; readonly toSeq: number }
  | { readonly type: 'response.submitted'; readonly response: Response }
  | {
      readonly type: 'item.graded';
      readonly correct: boolean;
      readonly score: number;
      readonly evidence: Evidence;
      readonly provisionalB: number;
      readonly envelope: readonly [number, number];
      readonly variantFeatures: Readonly<Partial<Record<DifficultySourceCode, 1>>>;
    }
  | { readonly type: 'item.abandoned'; readonly reason: 'timeout' | 'navigation' | 'closed' };

export type TelemetryEvent = EventEnvelope & TelemetryBody;

// ---------------------------------------------------------------------------
// Coletor
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'itens-iterativos:telemetry:v1';

export interface CollectorContext {
  readonly sessionId: string;
  readonly personId: string;
}

export class TelemetryCollector {
  #events: TelemetryEvent[] = [];
  #seq = 0;
  #t0 = 0;
  #attemptId = '';

  constructor(private readonly ctx: CollectorContext) {
    this.#restore();
  }

  startAttempt(attemptId: string): void {
    this.#attemptId = attemptId;
    this.#seq = 0;
    this.#t0 = now();
  }

  emit(
    body: TelemetryBody,
    item: { templateId: TemplateId; templateVersion: number; seed: Seed; instanceHash: string },
  ): TelemetryEvent {
    const ev = {
      v: 1,
      eventId: `${this.ctx.sessionId}-${this.#attemptId}-${this.#seq}`,
      seq: this.#seq++,
      tMs: Math.round(now() - this.#t0),
      emittedAt: new Date().toISOString(),
      sessionId: this.ctx.sessionId,
      attemptId: this.#attemptId,
      personId: this.ctx.personId,
      ...item,
      ...body,
    } as TelemetryEvent;

    this.#events.push(ev);
    this.#persist();
    return ev;
  }

  all(): readonly TelemetryEvent[] {
    return this.#events;
  }

  flush(): TelemetryEvent[] {
    const out = this.#events;
    this.#events = [];
    this.#persist();
    return out;
  }

  clear(): void {
    this.#events = [];
    this.#persist();
  }

  toJSON(): string {
    return JSON.stringify({ v: 1, events: this.#events }, null, 2);
  }

  #persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#events));
    } catch {
      // modo privado, cota estourada, armazenamento bloqueado: seguir sem persistir
    }
  }

  #restore(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) this.#events = JSON.parse(raw) as TelemetryEvent[];
    } catch {
      this.#events = [];
    }
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
